/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import endpointsTableData from "layouts/tables/data/endpointsTableData";
import fetchWithAuth from "layouts/tables/data/api"; // Import the custom fetch wrapper

// API Configuration
import appSettings from "appsettings.json";

function Endpoints() {
  const [expandedRow, setExpandedRow] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [newEndpoint, setNewEndpoint] = useState({
    name: "",
    description: "",
    displayName: "",
    isActive: true,
  });
  const [editEndpoint, setEditEndpoint] = useState({
    id: 0,
    name: "",
    description: "",
    isActive: true,
  });
  const [errors, setErrors] = useState({
    name: false,
    description: false,
    displayName: false,
  });

  const handleRowToggle = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const parseBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return false;
  };

  const fetchEndpoints = () => {
    setLoading(true);
    setError(null);
    const url = `${appSettings.baseUrl}${appSettings.endpoints.getAllEndpoints}`;
    fetchWithAuth(url) // Use the custom fetch wrapper
      .then((response) => {
        if (!response.ok) throw new Error("An error occurred while fetching endpoints.");
        return response.json();
      })
      .then((data) => {
        if (data && data.$values) {
          setEndpoints(data.$values);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEndpoints();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewEndpoint({ name: "", description: "", displayName: "", isActive: true });
    setErrors({ name: false, description: false, displayName: false });
  };

  const handleOpenEdit = (endpoint) => {
    setEditEndpoint({
      id: endpoint?.endpointId ?? endpoint?.id ?? endpoint?.Id ?? 0,
      name: endpoint?.endpointPath ?? endpoint?.name ?? endpoint?.Name ?? "",
      description:
        endpoint?.descriptionText ?? endpoint?.description ?? endpoint?.Description ?? "",
      isActive: parseBool(endpoint?.rawIsActive ?? endpoint?.isActive ?? endpoint?.IsActive),
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditEndpoint({ id: 0, name: "", description: "", isActive: true });
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setNewEndpoint((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear validation error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleEditChange = (e) => {
    const { name, value, checked, type } = e.target;
    setEditEndpoint((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = () => {
    // Validation logic
    const validationErrors = {
      name: !newEndpoint.name.trim(),
      description: !newEndpoint.description.trim(),
      displayName: !newEndpoint.displayName.trim(),
    };

    setErrors(validationErrors);

    // If any mandatory field is empty, stop the save process
    if (Object.values(validationErrors).some((isError) => isError)) {
      return;
    }

    setLoading(true);
    setError(null);
    const applicationId = endpoints[0]?.applicationId || endpoints[0]?.application?.id || 0;

    if (!applicationId) {
      setError("Application id is not available for this endpoint.");
      setLoading(false);
      return;
    }

    const url = `${appSettings.baseUrl}${appSettings.endpoints.createAppEndPoint}`;
    const payload = {
      ...newEndpoint,
      applicationId,
      isActive: newEndpoint.isActive ? 1 : 0,
    };

    fetchWithAuth(url, {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Failed to save endpoint";

          try {
            const errorData = await response.json();
            errorMessage =
              errorData?.title || errorData?.message || errorData?.error || errorMessage;
          } catch (e) {
            // Keep the default message if the error response is not JSON.
          }

          throw new Error(errorMessage);
        }
        handleClose();
        fetchEndpoints(); // Refresh the list after successful save
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  const handleSaveEdit = () => {
    if (!editEndpoint.id) {
      setError("Endpoint id is missing.");
      return;
    }

    if (!editEndpoint.description.trim()) {
      setError("Description is required.");
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.updateAppEndPoint}`;
    const payload = {
      id: editEndpoint.id,
      description: editEndpoint.description.trim(),
      isActive: editEndpoint.isActive ? 1 : 0,
    };

    fetchWithAuth(url, {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Failed to update endpoint";

          try {
            const errorData = await response.json();
            errorMessage =
              errorData?.title || errorData?.message || errorData?.error || errorMessage;
          } catch (e) {
            // Keep the default message if the error response is not JSON.
          }

          throw new Error(errorMessage);
        }

        setEndpoints((prev) =>
          prev.map((item) => {
            const itemId = item?.id ?? item?.Id ?? 0;
            if (String(itemId) !== String(editEndpoint.id)) {
              return item;
            }

            return {
              ...item,
              description: editEndpoint.description.trim(),
              Description: editEndpoint.description.trim(),
              isActive: editEndpoint.isActive,
              IsActive: editEndpoint.isActive,
            };
          })
        );

        handleCloseEdit();
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  const { columns, rows } = endpointsTableData(
    endpoints,
    handleRowToggle,
    expandedRow,
    handleOpenEdit
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Endpoints
                </MDTypography>
                <MDButton variant="gradient" color="success" onClick={handleOpen}>
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  &nbsp;add new endpoint
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress color="success" />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }} // Changed to light red background
                        borderRadius="lg"
                        // coloredShadow="error" // Removed as it might not match the custom background color
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          <Icon fontSize="small">close</Icon>
                        </MDTypography>
                      </MDBox>
                    )}
                    <DataTable
                      table={{ columns, rows }}
                      canSearch
                      isSorted={true}
                      entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20] }}
                      showTotalEntries={true}
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New Endpoint</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" pt={2} pb={1}>
            <MDBox mb={2}>
              <TextField
                name="displayName"
                label="Display Name"
                fullWidth
                value={newEndpoint.displayName}
                onChange={handleChange}
                error={errors.displayName}
                helperText={errors.displayName && "Display Name is required"}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                name="name"
                label="Endpoint Name (Path)"
                fullWidth
                value={newEndpoint.name}
                onChange={handleChange}
                error={errors.name}
                helperText={errors.name && "Endpoint Path is required"}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newEndpoint.description}
                onChange={handleChange}
                error={errors.description}
                helperText={errors.description && "Description is required"}
              />
            </MDBox>
            <FormControlLabel
              control={
                <Switch name="isActive" checked={newEndpoint.isActive} onChange={handleChange} />
              }
              label="Is Active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleClose} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSave} variant="gradient" color="success" disabled={loading}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Endpoint</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" pt={2} pb={1}>
            <MDBox mb={2}>
              <TextField
                name="name"
                label="Endpoint Name (Path)"
                fullWidth
                value={editEndpoint.name}
                InputProps={{ readOnly: true }}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={editEndpoint.description}
                onChange={handleEditChange}
              />
            </MDBox>
            <FormControlLabel
              control={
                <Switch
                  name="isActive"
                  checked={editEndpoint.isActive}
                  onChange={handleEditChange}
                />
              }
              label="Is Active"
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseEdit} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSaveEdit} variant="gradient" color="success" disabled={loading}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Endpoints;
